import * as React from 'react';
import { DialerPhoneCallStatus, useTelephony } from '../../../contexts/telephony';
import { useQueue } from '../../../hooks/queue';
import { useTysonCoaching } from '../../contexts/TysonCoachingContext';
import { useRealCallCoaching } from '../../hooks/useRealCallCoaching';
import { useRealCallCoachingSession } from '../../hooks/useRealCallCoachingSession';
import { TranscriptEntry } from '../../types';

/**
 * Bridge component that connects real Twilio calls to the coaching system.
 * Renders nothing - purely handles side effects.
 */
export const RealCallCoachingBridge: React.FC = () => {
	const { phoneCall, phoneCallStatus } = useTelephony();
	const queue = useQueue();
	const coaching = useTysonCoaching();
	const [hasRequestedSummary, setHasRequestedSummary] = React.useState(false);

	// Extract stable references to avoid dependency issues
	const registerFlushCallback = coaching.registerFlushCallback;
	const setLiveUtterances = coaching.setLiveUtterances;
	const sendTranscriptBatchImmediately = coaching.sendTranscriptBatchImmediately;

	const prevStatusRef = React.useRef<DialerPhoneCallStatus>(phoneCallStatus);
	// Store flush callback in ref so we can call it at end of call without effect dependencies
	const flushCallbackRef = React.useRef<(() => TranscriptEntry[]) | null>(null);

	const { requestEndOfCallSummary, isReady } = useRealCallCoachingSession({
		companyId: queue?.companyId || phoneCall?.companyId || null,
		contactId: phoneCall?.contactIds?.[0] || null,
		leadServedId: queue?.leadServedId,
		phoneCallId: phoneCall?.id,
	});

	// Bridge transcription events to coaching when call is connected
	const isCallActive = phoneCallStatus === DialerPhoneCallStatus.Connected;

	const { liveUtterances, flushAndGetEntries } = useRealCallCoaching({
		phoneCallId: phoneCall?.id || null,
		enabled: isCallActive && isReady,
	});

	// Keep flush callback ref updated while call is active
	React.useEffect(() => {
		flushCallbackRef.current = isCallActive && isReady ? flushAndGetEntries : null;
	}, [isCallActive, isReady, flushAndGetEntries]);

	// Register flush callback with context (for "Get Suggestion" button)
	React.useEffect(() => {
		if (isCallActive && isReady) {
			registerFlushCallback(flushAndGetEntries);
		} else {
			registerFlushCallback(null);
		}

		return () => {
			registerFlushCallback(null);
		};
	}, [isCallActive, isReady, flushAndGetEntries, registerFlushCallback]);

	// Sync live utterances to context
	React.useEffect(() => {
		setLiveUtterances(liveUtterances);
	}, [liveUtterances, setLiveUtterances]);

	// Flush remaining transcript and request end-of-call summary when call ends
	React.useEffect(() => {
		// Detect transition: was Connected, now NoCall
		const wasConnected = prevStatusRef.current === DialerPhoneCallStatus.Connected;
		const isNowEnded = phoneCallStatus === DialerPhoneCallStatus.NoCall;

		if (wasConnected && isNowEnded && isReady && !hasRequestedSummary) {
			// Flush any remaining transcript entries before requesting summary.
			// Use the ref to get the callback - it was captured while call was active.
			if (flushCallbackRef.current) {
				const remainingEntries = flushCallbackRef.current();
				if (remainingEntries && remainingEntries.length > 0) {
					sendTranscriptBatchImmediately(remainingEntries);
				}
				flushCallbackRef.current = null;
			}

			requestEndOfCallSummary();
			setHasRequestedSummary(true);
		}

		prevStatusRef.current = phoneCallStatus;
	}, [phoneCallStatus, isReady, hasRequestedSummary, requestEndOfCallSummary, sendTranscriptBatchImmediately]);

	// Reset summary flag when new call starts
	React.useEffect(() => {
		if (phoneCallStatus === DialerPhoneCallStatus.Dialing) {
			setHasRequestedSummary(false);
		}
	}, [phoneCallStatus]);

	return null;
};
